import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  TextField,
  Typography,
  useTheme,
  alpha,
  Grid,
  IconButton,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  ListItemText,
  Chip,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { createQuiz, updateQuiz, getQuiz, clearQuiz } from "../../state/slices/quiz";
import { getStudents } from "../../state/slices/tutor";
import { Add, Delete, Quiz, ArrowBack, Save } from "@mui/icons-material";
import Loading from "../../components/loading/loading";
import ethiopiaCurriculum from "../../utils/ethiopiaCurriculum.json";

const QUESTION_TYPES = ["multipleChoice", "trueFalse", "shortAnswer", "essay"];
const QUIZ_STATUS = ["draft", "published", "archived"];

// Question type labels mapping
const getQuestionTypeLabel = (type, t) => {
  const labels = {
    multipleChoice: t('quiz.questionTypeMultipleChoice'),
    trueFalse: t('quiz.questionTypeTrueFalse'),
    shortAnswer: t('quiz.questionTypeShortAnswer'),
    essay: t('quiz.questionTypeEssay'),
  };
  return labels[type] || type;
};

const CreateQuiz = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentQuiz, loading, successMsg } = useSelector((state) => state.quiz);
  
  const editingQuiz = location.state?.quiz;
  const user = useSelector((state) => state.user.user);
  const { students } = useSelector((state) => state.tutor);
  const isTutor = user?.roles?.includes("TUTOR");
  
  // Root navigation paths - don't show back button on these
  const rootPaths = ['/quiz/my', '/quiz/student', '/flashcard/my', '/flashcard/student', '/progress', '/', '/request', '/student-request', '/tutor-class-room', '/student-class-room'];
  const showBackButton = !rootPaths.includes(location.pathname);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    subject: "",
    gradeLevel: "",
    questions: [],
    timeLimit: null,
    status: "draft",
    assignedStudents: [],
  });
  
  const [availableSubjects, setAvailableSubjects] = useState([]);

  useEffect(() => {
    if (isTutor) {
      dispatch(getStudents());
    }
  }, [isTutor, dispatch]);

  useEffect(() => {
    if (editingQuiz) {
      setFormData({
        title: editingQuiz.title || "",
        description: editingQuiz.description || "",
        subject: editingQuiz.subject || "",
        gradeLevel: editingQuiz.gradeLevel || "",
        questions: editingQuiz.questions || [],
        timeLimit: editingQuiz.timeLimit || null,
        status: editingQuiz.status || "draft",
        assignedStudents: editingQuiz.assignedStudents?.map(s => s._id || s) || [],
      });
    }
  }, [editingQuiz]);

  useEffect(() => {
    if (formData.gradeLevel && ethiopiaCurriculum.subjects[formData.gradeLevel]) {
      setAvailableSubjects(ethiopiaCurriculum.subjects[formData.gradeLevel]);
    } else {
      setAvailableSubjects([]);
    }
  }, [formData.gradeLevel]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleQuestionChange = (index, field, value) => {
    const newQuestions = [...formData.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setFormData({ ...formData, questions: newQuestions });
  };

  const addQuestion = () => {
    setFormData({
      ...formData,
      questions: [
        ...formData.questions,
        {
          type: "multipleChoice",
          question: "",
          options: ["", "", "", ""],
          correctAnswer: "",
          points: 1,
          explanation: "",
        },
      ],
    });
  };

  const removeQuestion = (index) => {
    const newQuestions = formData.questions.filter((_, i) => i !== index);
    setFormData({ ...formData, questions: newQuestions });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Strip _id from questions
    const submitData = {
      ...formData,
      questions: formData.questions.map(q => {
        const { _id, ...question } = q;
        return question;
      })
    };
    
    if (editingQuiz) {
      dispatch(updateQuiz({ quizId: editingQuiz._id, quizData: submitData })).then(() => {
        navigate("/quiz/my");
      });
    } else {
      dispatch(createQuiz(submitData)).then(() => {
        navigate("/quiz/my");
      });
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        pb: 4,
      }}
    >
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Back Button - only show if not on root navigation page */}
        {showBackButton && (
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: 'text.secondary',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              },
            }}
          >
            {t('common.back')}
          </Button>
        )}

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 3,
                bgcolor: theme.palette.primary.main,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Quiz sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 0.5 }}>
                {editingQuiz ? t('quiz.editQuiz') : t('quiz.createNewQuiz')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editingQuiz ? t('quiz.editQuizDesc') || 'Update your quiz details and questions' : t('quiz.createNewQuizDesc') || 'Create a new quiz with multiple question types'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Card 
            elevation={0}
            sx={{ 
              mb: 3,
              borderRadius: 3,
              bgcolor: 'white',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('quiz.quizTitle')}
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('quiz.description')}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('quiz.gradeLevel')}</InputLabel>
                    <Select
                      name="gradeLevel"
                      value={formData.gradeLevel}
                      onChange={handleChange}
                      label={t('quiz.gradeLevel')}
                    >
                      {ethiopiaCurriculum.gradeLevels.map((grade) => (
                        <MenuItem key={grade} value={grade}>
                          {grade}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('common.subject')}</InputLabel>
                    <Select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      label={t('common.subject')}
                      disabled={!formData.gradeLevel}
                    >
                      {availableSubjects.map((subject) => (
                        <MenuItem key={subject} value={subject}>
                          {subject}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label={t('quiz.timeLimit')}
                    name="timeLimit"
                    value={formData.timeLimit || ""}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('quiz.status')}</InputLabel>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      label={t('quiz.status')}
                    >
                      {QUIZ_STATUS.map((status) => (
                        <MenuItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {isTutor && students && students.length > 0 && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>{t('quiz.assignToStudents')}</InputLabel>
                      <Select
                        multiple
                        value={formData.assignedStudents}
                        onChange={(e) => setFormData({ ...formData, assignedStudents: e.target.value })}
                        label={t('quiz.assignToStudents')}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((studentId) => {
                              const student = students.find(s => s.student._id === studentId || s.student.id === studentId);
                              return (
                                <Chip
                                  key={studentId}
                                  label={student ? `${student.student.firstName} ${student.student.lastName}` : studentId}
                                  size="small"
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        {students.map((studentData) => {
                          const studentId = studentData.student._id || studentData.student.id;
                          return (
                            <MenuItem key={studentId} value={studentId}>
                              <Checkbox checked={formData.assignedStudents.includes(studentId)} />
                              <ListItemText primary={`${studentData.student.firstName} ${studentData.student.lastName}`} />
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              bgcolor: 'white',
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {t('quiz.questions')}
                </Typography>
                <Button 
                  startIcon={<Add />} 
                  onClick={addQuestion}
                  variant="contained"
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    fontWeight: 600,
                  }}
                >
                  {t('quiz.addQuestion')}
                </Button>
              </Box>

              {formData.questions.map((question, index) => (
                <Card 
                  key={index} 
                  elevation={0}
                  sx={{ 
                    mb: 2, 
                    p: 3,
                    borderRadius: 2,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Typography variant="subtitle1">{t('quiz.question')} {index + 1}</Typography>
                    <IconButton onClick={() => removeQuestion(index)} color="error">
                      <Delete />
                    </IconButton>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>{t('quiz.questionType')}</InputLabel>
                        <Select
                          value={question.type}
                          onChange={(e) => handleQuestionChange(index, "type", e.target.value)}
                          label={t('quiz.questionType')}
                        >
                          {QUESTION_TYPES.map((type) => (
                            <MenuItem key={type} value={type}>
                              {getQuestionTypeLabel(type, t)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('quiz.question')}
                        value={question.question}
                        onChange={(e) => handleQuestionChange(index, "question", e.target.value)}
                        required
                      />
                    </Grid>
                    {question.type === "multipleChoice" && (
                      <>
                        {question.options.map((option, optIndex) => (
                          <Grid item xs={12} sm={6} key={optIndex}>
                            <TextField
                              fullWidth
                              label={`${t('quiz.option')} ${optIndex + 1}`}
                              value={option}
                              onChange={(e) => {
                                const newOptions = [...question.options];
                                newOptions[optIndex] = e.target.value;
                                handleQuestionChange(index, "options", newOptions);
                              }}
                            />
                          </Grid>
                        ))}
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={t('quiz.correctAnswerMultipleChoice')}
                            value={question.correctAnswer}
                            onChange={(e) => handleQuestionChange(index, "correctAnswer", e.target.value)}
                            required
                          />
                        </Grid>
                      </>
                    )}
                    {question.type === "trueFalse" && (
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>{t('quiz.correctAnswer')}</InputLabel>
                          <Select
                            value={question.correctAnswer === true || question.correctAnswer === "true" ? "true" : "false"}
                            onChange={(e) => handleQuestionChange(index, "correctAnswer", e.target.value === "true")}
                            label={t('quiz.correctAnswer')}
                          >
                            <MenuItem value="true">{t('quiz.true')}</MenuItem>
                            <MenuItem value="false">{t('quiz.false')}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {question.type === "shortAnswer" && (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t('quiz.correctAnswer')}
                          value={question.correctAnswer}
                          onChange={(e) => handleQuestionChange(index, "correctAnswer", e.target.value)}
                          required
                        />
                      </Grid>
                    )}
                    {/* Only show points field for questions that are auto-graded (multipleChoice and trueFalse) */}
                    {(question.type === "multipleChoice" || question.type === "trueFalse") && (
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label={t('quiz.points')}
                          value={question.points}
                          onChange={(e) => handleQuestionChange(index, "points", parseInt(e.target.value))}
                          inputProps={{ min: 1 }}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('quiz.explanationOptional')}
                        value={question.explanation || ""}
                        onChange={(e) => handleQuestionChange(index, "explanation", e.target.value)}
                        multiline
                        rows={2}
                      />
                    </Grid>
                  </Grid>
                </Card>
              ))}

              {formData.questions.length === 0 && (
                <Typography align="center" color="textSecondary" sx={{ py: 4 }}>
                  {t('quiz.noQuestionsAdded')}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate("/quiz/my")}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={formData.questions.length === 0}
              startIcon={<Save />}
              sx={{
                borderRadius: 2,
                px: 4,
                py: 1.5,
                fontWeight: 600,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {editingQuiz ? t('quiz.updateQuiz') : t('quiz.createQuiz')}
            </Button>
          </Box>
        </form>
      </Container>
    </Box>
  );
};

export default CreateQuiz;

